import { Button, Drawer, Flex, Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { TextInput } from 'react-hook-form-mantine';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { useTranslationOrganization } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useCreateOrganization } from '@dealstack/frontend/app/pages/Organizations/hooks/useCreateOrganization';
import {
	CREATE_ORGANIZATION_FORM,
	CreateOrganizationFormValues,
	CreateOrganizationShape,
} from '@dealstack/frontend/app/pages/Organizations/components/CreateOrganizationDrawer/types';
import { parseEmailDomains } from '@dealstack/frontend/app/pages/Organizations/utils/emailDomains';

interface Props {
	opened: boolean;
	onClose: () => void;
}

export const CreateOrganizationDrawer = ({ opened, onClose }: Props) => {
	const tOrganization = useTranslationOrganization();
	const { mutate: createOrg, isPending } = useCreateOrganization();

	const form = useForm<CreateOrganizationFormValues>({
		mode: 'all',
		resolver: zodResolver(CreateOrganizationShape()),
		defaultValues: {
			[CREATE_ORGANIZATION_FORM.NAME]: '',
			[CREATE_ORGANIZATION_FORM.OWNER_EMAIL]: '',
			[CREATE_ORGANIZATION_FORM.CONTACT_EMAIL]: '',
			[CREATE_ORGANIZATION_FORM.EMAIL_DOMAINS]: '',
		},
	});

	useEffect(() => {
		if (opened) {
			form.reset();
		}
	}, [opened, form]);

	const onSubmit = (values: CreateOrganizationFormValues) => {
		const emailDomains = parseEmailDomains(values[CREATE_ORGANIZATION_FORM.EMAIL_DOMAINS]);
		createOrg(
			{
				name: values[CREATE_ORGANIZATION_FORM.NAME],
				ownerEmail: values[CREATE_ORGANIZATION_FORM.OWNER_EMAIL],
				contactEmail: values[CREATE_ORGANIZATION_FORM.CONTACT_EMAIL] || undefined,
				emailDomains: emailDomains.length > 0 ? emailDomains : undefined,
			},
			{ onSuccess: onClose }
		);
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tOrganization('organizations_management.create.drawer.title')}
		>
			<FormProvider {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} style={{ height: '100%' }}>
					<Flex direction="column" justify="space-between" h="100%" gap="1rem">
						<Stack gap="md">
							<TextInput
								name={CREATE_ORGANIZATION_FORM.NAME}
								size="md"
								label={tOrganization(
									'organizations_management.create.drawer.fields.name.label'
								)}
								placeholder={tOrganization(
									'organizations_management.create.drawer.fields.name.placeholder'
								)}
							/>
							<TextInput
								name={CREATE_ORGANIZATION_FORM.OWNER_EMAIL}
								size="md"
								label={tOrganization(
									'organizations_management.create.drawer.fields.owner_email.label'
								)}
								placeholder={tOrganization(
									'organizations_management.create.drawer.fields.owner_email.placeholder'
								)}
							/>
							<TextInput
								name={CREATE_ORGANIZATION_FORM.CONTACT_EMAIL}
								size="md"
								label={tOrganization(
									'organizations_management.create.drawer.fields.contact_email.label'
								)}
								placeholder={tOrganization(
									'organizations_management.create.drawer.fields.contact_email.placeholder'
								)}
							/>
							<TextInput
								name={CREATE_ORGANIZATION_FORM.EMAIL_DOMAINS}
								size="md"
								label={tOrganization(
									'organizations_management.create.drawer.fields.email_domains.label'
								)}
								placeholder={tOrganization(
									'organizations_management.create.drawer.fields.email_domains.placeholder'
								)}
							/>
						</Stack>
						<Button
							type="submit"
							disabled={
								isPending || !form.formState.isValid || !form.formState.isDirty
							}
						>
							{tOrganization('organizations_management.create.drawer.submit')}
						</Button>
					</Flex>
				</form>
			</FormProvider>
		</Drawer>
	);
};
